import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

const PrivacyPolicyCopDodger: React.FC = () => {
  const { t, i18n } = useTranslation('privacy-policy-copdodger');

  const currentLang = i18n.language ? i18n.language.substring(0, 2) : 'tr';

  return (
    <div className="max-w-3xl mx-auto p-6 text-gray-200 dark:bg-black dark:text-white font-sans">
      {/* Language Switcher */}
      <div className="flex justify-end items-center mb-4 space-x-2 text-sm">
        <span className="text-gray-400">Language:</span>
        <button
          onClick={() => i18n.changeLanguage('tr')}
          className={`px-2.5 py-1 rounded font-medium transition-colors ${
            currentLang === 'tr'
              ? 'bg-[#00df9a] text-black font-semibold'
              : 'bg-gray-800 text-gray-300 hover:text-white hover:bg-gray-700'
          }`}
        >
          TR
        </button>
        <button
          onClick={() => i18n.changeLanguage('en')}
          className={`px-2.5 py-1 rounded font-medium transition-colors ${
            currentLang === 'en'
              ? 'bg-[#00df9a] text-black font-semibold'
              : 'bg-gray-800 text-gray-300 hover:text-white hover:bg-gray-700'
          }`}
        >
          EN
        </button>
      </div>

      {/* Title */}
      <h1 className="text-3xl font-bold mb-3 text-gray-100 dark:text-white">
        {t('copdodger.title')}
      </h1>

      {/* Package Badge */}
      <div className="mb-4">
        <span className="inline-block bg-gray-800 border border-gray-700 text-xs font-mono text-[#00df9a] px-2.5 py-1 rounded">
          {t('copdodger.packageLabel')} {t('copdodger.packageName')}
        </span>
      </div>

      {/* Last Updated & Effective Date */}
      <div className="mb-6 text-sm text-gray-300 dark:text-white space-y-1">
        <p>
          <strong className="text-gray-200 dark:text-white">{t('copdodger.lastUpdatedLabel')}</strong>
          {t('copdodger.lastUpdated')}
        </p>
        <p>
          <strong className="text-gray-200 dark:text-white">{t('copdodger.effectiveDateLabel')}</strong>
          {t('copdodger.effectiveDate')}
        </p>
      </div>

      {/* Intro */}
      <div className="mb-6 space-y-4 leading-relaxed text-gray-300 dark:text-white">
        <p>
          <Trans
            t={t}
            i18nKey="copdodger.intro1"
            components={[
              <React.Fragment key="0" />,
              <strong key="1" className="font-semibold text-[#00df9a]" />,
              <code key="2" className="bg-gray-800 text-gray-200 px-1.5 py-0.5 rounded text-xs font-mono" />
            ]}
          />
        </p>
        <p>
          <Trans
            t={t}
            i18nKey="copdodger.intro2"
            components={[
              <React.Fragment key="0" />,
              <strong key="1" className="font-semibold text-gray-100 dark:text-white" />
            ]}
          />
        </p>
        <p>
          {t('copdodger.intro3')}
        </p>
      </div>

      <hr className="border-gray-700 my-6" />

      {/* Section 1: Toplamadığımız Veriler */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section1Title')}
        </h2>
        <p className="mb-3 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section1Intro')}
        </p>
        <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white mb-4">
          <li>{t('copdodger.section1Item1')}</li>
          <li>{t('copdodger.section1Item2')}</li>
          <li>{t('copdodger.section1Item3')}</li>
          <li>{t('copdodger.section1Item4')}</li>
        </ul>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 2: Otomatik Olarak Toplanan ve İşlenen Veriler */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section2Title')}
        </h2>
        <p className="mb-4 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section2Intro')}
        </p>

        {/* 2.a Yerel Oyun İlerlemesi ve Ayarlar */}
        <div className="mb-6 pl-3 border-l-2 border-emerald-500/70">
          <h3 className="text-xl font-medium mb-2 text-gray-200 dark:text-white">
            {t('copdodger.section2ATitle')}
          </h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white">
            <li>{t('copdodger.section2AItem1')}</li>
            <li>{t('copdodger.section2AItem2')}</li>
          </ul>
        </div>

        {/* 2.b Cihaz ve Kullanım Verileri */}
        <div className="mb-4 pl-3 border-l-2 border-emerald-500/70">
          <h3 className="text-xl font-medium mb-2 text-gray-200 dark:text-white">
            {t('copdodger.section2BTitle')}
          </h3>
          <p className="mb-3 leading-relaxed text-gray-300 dark:text-white">
            {t('copdodger.section2BIntro')}
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white">
            <li>{t('copdodger.section2BItem1')}</li>
            <li>{t('copdodger.section2BItem2')}</li>
            <li>{t('copdodger.section2BItem3')}</li>
            <li>{t('copdodger.section2BItem4')}</li>
            <li>{t('copdodger.section2BItem5')}</li>
            <li>{t('copdodger.section2BItem6')}</li>
          </ul>
        </div>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 3: Bilgilerinizi Nasıl Kullanıyoruz? */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section3Title')}
        </h2>
        <p className="mb-4 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section3Intro')}
        </p>

        <ul className="space-y-3 pl-2 text-gray-300 dark:text-white">
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item1Title')} </strong>
              {t('copdodger.section3Item1Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item2Title')} </strong>
              {t('copdodger.section3Item2Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item3Title')} </strong>
              {t('copdodger.section3Item3Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item4Title')} </strong>
              {t('copdodger.section3Item4Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item5Title')} </strong>
              {t('copdodger.section3Item5Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item6Title')} </strong>
              {t('copdodger.section3Item6Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item7Title')} </strong>
              {t('copdodger.section3Item7Desc')}
            </div>
          </li>
          <li className="flex items-start">
            <span className="text-[#00df9a] mr-2">▪</span>
            <div>
              <strong className="text-gray-100 dark:text-white">{t('copdodger.section3Item8Title')} </strong>
              {t('copdodger.section3Item8Desc')}
            </div>
          </li>
        </ul>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 4: Kullanılan Üçüncü Taraf Servisler ve SDK'lar */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section4Title')}
        </h2>
        <p className="mb-4 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section4Intro')}
        </p>

        <div className="space-y-4">
          {/* 1. Google AdMob */}
          <div className="p-4 bg-gray-800/60 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-2 text-[#00df9a]">
              {t('copdodger.section4_1Title')}
            </h3>
            <p className="mb-1 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_1Purpose')}</p>
            <p className="mb-3 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_1Data')}</p>
            <div className="text-sm">
              <span className="font-medium text-gray-300">{t('copdodger.section4_1Policy')} </span>
              <a
                href={t('copdodger.section4_1PolicyLink')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00df9a] hover:underline"
              >
                {t('copdodger.section4_1PolicyLink')}
              </a>
            </div>
          </div>

          {/* 2. Firebase */}
          <div className="p-4 bg-gray-800/60 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-2 text-[#00df9a]">
              {t('copdodger.section4_2Title')}
            </h3>
            <p className="mb-1 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_2Purpose')}</p>
            <p className="mb-3 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_2Data')}</p>
            <div className="text-sm">
              <span className="font-medium text-gray-300">{t('copdodger.section4_2Policy')} </span>
              <a
                href={t('copdodger.section4_2PolicyLink')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00df9a] hover:underline"
              >
                {t('copdodger.section4_2PolicyLink')}
              </a>
            </div>
          </div>

          {/* 3. RevenueCat */}
          <div className="p-4 bg-gray-800/60 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-2 text-[#00df9a]">
              {t('copdodger.section4_3Title')}
            </h3>
            <p className="mb-1 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_3Purpose')}</p>
            <p className="mb-3 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_3Data')}</p>
            <div className="text-sm">
              <span className="font-medium text-gray-300">{t('copdodger.section4_3Policy')} </span>
              <a
                href={t('copdodger.section4_3PolicyLink')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00df9a] hover:underline"
              >
                {t('copdodger.section4_3PolicyLink')}
              </a>
            </div>
          </div>

          {/* 4. Sentry */}
          <div className="p-4 bg-gray-800/60 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-2 text-[#00df9a]">
              {t('copdodger.section4_4Title')}
            </h3>
            <p className="mb-1 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_4Purpose')}</p>
            <p className="mb-3 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_4Data')}</p>
            <div className="text-sm">
              <span className="font-medium text-gray-300">{t('copdodger.section4_4Policy')} </span>
              <a
                href={t('copdodger.section4_4PolicyLink')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00df9a] hover:underline"
              >
                {t('copdodger.section4_4PolicyLink')}
              </a>
            </div>
          </div>

          {/* 5. Google Play Games & Apple Game Center */}
          <div className="p-4 bg-gray-800/60 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-2 text-[#00df9a]">
              {t('copdodger.section4_5Title')}
            </h3>
            <p className="mb-1 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_5Purpose')}</p>
            <p className="mb-3 text-sm text-gray-300 dark:text-white">{t('copdodger.section4_5Data')}</p>
            <div className="text-sm space-x-4">
              <span className="font-medium text-gray-300">{t('copdodger.section4_5Policy')}</span>
              <a
                href={t('copdodger.section4_5GoogleLink')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00df9a] hover:underline"
              >
                {t('copdodger.section4_5GooglePolicy')}
              </a>
              <span className="text-gray-500">•</span>
              <a
                href={t('copdodger.section4_5AppleLink')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00df9a] hover:underline"
              >
                {t('copdodger.section4_5ApplePolicy')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 5: Reklamlar ve İzin / Rıza Yönetimi (GDPR / UMP) */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section5Title')}
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white mb-4">
          <li>{t('copdodger.section5Item1')}</li>
          <li>{t('copdodger.section5Item2')}</li>
          <li>{t('copdodger.section5Item3')}</li>
          <li>
            {t('copdodger.section5Item4')}
            <ul className="list-disc pl-6 mt-1 space-y-1 text-sm text-gray-400">
              <li>{t('copdodger.section5Item4_Android')}</li>
              <li>{t('copdodger.section5Item4_iOS')}</li>
            </ul>
          </li>
        </ul>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 6: Uygulama İçi Satın Alımlar ve Ödemeler */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section6Title')}
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white">
          <li>{t('copdodger.section6Item1')}</li>
          <li>{t('copdodger.section6Item2')}</li>
          <li>{t('copdodger.section6Item3')}</li>
        </ul>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 7: Destek Talepleri */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section7Title')}
        </h2>
        <p className="leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section7Text')}
        </p>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 8: Veri Saklama ve Silme */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section8Title')}
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white">
          <li>{t('copdodger.section8Item1')}</li>
          <li>{t('copdodger.section8Item2')}</li>
          <li>{t('copdodger.section8Item3')}</li>
        </ul>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 9: Çocukların Gizliliği (COPPA ve GDPR-K) */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section9Title')}
        </h2>
        <p className="mb-3 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section9Text1')}
        </p>
        <p className="leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section9Text2')}
        </p>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 10: Haklarınız (KVKK, GDPR, CCPA/CPRA) */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section10Title')}
        </h2>
        <p className="mb-3 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section10Intro')}
        </p>
        <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white mb-4">
          <li>{t('copdodger.section10Item1')}</li>
          <li>{t('copdodger.section10Item2')}</li>
          <li>{t('copdodger.section10Item3')}</li>
          <li>{t('copdodger.section10Item4')}</li>
        </ul>
        <p className="leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section10Footer')}
        </p>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 11: Veri Güvenliği */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section11Title')}
        </h2>
        <p className="leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section11Text')}
        </p>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 12: Gizlilik Politikasındaki Değişiklikler */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section12Title')}
        </h2>
        <p className="leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section12Text')}
        </p>
      </section>

      <hr className="border-gray-700 my-6" />

      {/* Section 13: İletişim Bilgileri */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3 text-gray-200 dark:text-white">
          {t('copdodger.section13Title')}
        </h2>
        <p className="mb-3 leading-relaxed text-gray-300 dark:text-white">
          {t('copdodger.section13Text')}
        </p>
        <ul className="list-disc pl-6 space-y-2 text-gray-300 dark:text-white">
          <li>
            <strong className="text-gray-200 dark:text-white">{t('copdodger.contactDeveloper')} </strong>
            {t('copdodger.contactDeveloperVal')}
          </li>
          <li>
            <strong className="text-gray-200 dark:text-white">{t('copdodger.contactEmail')} </strong>
            <a
              href={`mailto:${t('copdodger.contactEmailVal')}`}
              className="text-[#00df9a] hover:underline"
            >
              {t('copdodger.contactEmailVal')}
            </a>
          </li>
          <li>
            <strong className="text-gray-200 dark:text-white">{t('copdodger.contactWebsite')} </strong>
            <a
              href={t('copdodger.contactWebsiteVal')}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00df9a] hover:underline"
            >
              {t('copdodger.contactWebsiteVal')}
            </a>
          </li>
          <li>
            <strong className="text-gray-200 dark:text-white">{t('copdodger.contactPolicyUrl')} </strong>
            <a
              href={t('copdodger.contactPolicyUrlVal')}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00df9a] hover:underline"
            >
              {t('copdodger.contactPolicyUrlVal')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default PrivacyPolicyCopDodger;
